import { Injectable } from '@angular/core';
import mapboxgl from 'mapbox-gl';
import { BaseEntityLayerManager } from '../core/base/base-entity-layer-manager';
import { IStatefulEntity } from '../core/interfaces/entity-layer-manager.interface';
import { IGateway } from 'modelos/src';

/**
 * Estados de salud de un gateway LoRaWAN
 */
export type EstadoSaludGateway = 'ok' | 'warning' | 'error' | 'unknown';

/**
 * GatewaysLayerManager
 *
 * Manager para renderizar gateways LoRaWAN en el mapa.
 *
 * Características:
 * - Estados dinámicos de salud (ok, warning, error)
 * - Iconos SVG según estado de salud
 * - NO soporta clustering (típicamente hay pocos gateways)
 * - Implementa IStatefulEntity para manejo de estados
 *
 * Iconos:
 * - gateway-ok.svg (ok - ToA < 5%)
 * - gateway-warning.svg (warning - ToA 5-17%)
 * - gateway-error.svg (error - ToA > 17%)
 * - gateway.svg (default/unknown)
 */
@Injectable({
  providedIn: 'root',
})
export class GatewaysLayerManager
  extends BaseEntityLayerManager<IGateway>
  implements IStatefulEntity<EstadoSaludGateway>
{
  // ========== CONFIGURACIÓN ==========
  readonly entityType = 'gateways';
  readonly supportsClustering = false;
  readonly clusterColor: string | undefined = undefined; // Típicamente hay pocos gateways

  // IDs de layers
  private mainLayerId = 'gateways-layer';

  // ========== MAPEO DE ESTADOS ==========

  readonly stateToIconMap: Record<EstadoSaludGateway, string> = {
    ok: 'gateway-ok-icon',
    warning: 'gateway-warning-icon',
    error: 'gateway-error-icon',
    unknown: 'gateway-default-icon',
  };

  // ========== RENDERIZACIÓN ==========

  /**
   * Convierte un gateway a un Feature de GeoJSON
   */
  override entityToFeature(gateway: IGateway): GeoJSON.Feature<GeoJSON.Point> {
    // OPTIMIZACIÓN: Almacenar en mapa en vez de serializar
    this.storeEntity(gateway);

    // Determinar estado de salud
    const estado = this.determineEntityState(gateway);

    // Obtener coordenadas
    let coordinates: [number, number];

    if (gateway.ubicacion?.coordinates) {
      coordinates = gateway.ubicacion.coordinates as [number, number];
    } else {
      // Fallback: Buenos Aires
      console.warn(`⚠️ Gateway ${gateway._id} sin coordenadas válidas`);
      coordinates = [-58.443832, -34.587386];
    }

    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates,
      },
      properties: {
        id: gateway._id,
        gatewayEui: gateway.gatewayEui,
        nombre: gateway.nombre || gateway.nombreChirpstack,
        estadoSalud: estado,
        toaPromedio: gateway.toaPromedioHora,
        // Datos completos
      },
    };
  }

  /**
   * Determina el estado de salud de un gateway
   *
   * Estados basados en ToA (Time on Air):
   * - ok: ToA < 5%
   * - warning: ToA 5-17%
   * - error: ToA > 17%
   * - unknown: Sin datos
   */
  determineEntityState(gateway: IGateway): EstadoSaludGateway {
    // Si viene con estado de salud calculado, usarlo
    if (gateway.estadoSalud) {
      return gateway.estadoSalud as EstadoSaludGateway;
    }

    // Si tiene ToA promedio, calcular estado
    if (
      gateway.toaPromedioHora !== undefined &&
      gateway.toaPromedioHora !== null
    ) {
      if (gateway.toaPromedioHora < 5) {
        return 'ok';
      } else if (gateway.toaPromedioHora < 17) {
        return 'warning';
      } else {
        return 'error';
      }
    }

    // Default: unknown
    return 'unknown';
  }

  /**
   * Expresión para el icono (dinámica según estado de salud)
   */
  override getIconExpression(): mapboxgl.Expression {
    return [
      'match',
      ['get', 'estadoSalud'],
      'ok',
      this.stateToIconMap['ok'],
      'warning',
      this.stateToIconMap['warning'],
      'error',
      this.stateToIconMap['error'],
      this.stateToIconMap['unknown'], // Default
    ];
  }

  // ========== CREACIÓN DE LAYERS ==========

  /**
   * Crea el layer para gateways
   *
   * Nota: Los gateways NO tienen clustering ya que típicamente son
   * pocos dispositivos (10-50 gateways por cliente).
   */
  protected override createLayers(): void {
    if (!this.map) return;

    // MAIN LAYER (único layer, sin clustering)
    this.addLayer({
      id: this.mainLayerId,
      type: 'symbol',
      source: this.sourceId,
      layout: {
        'icon-image': this.getIconExpression(),
        'icon-size': 0.044, // SVG 800x800 → 35px (35/800 = 0.044)
        'icon-allow-overlap': true,
        'icon-padding': 0,
      },
    });
  }

  // ========== VISIBILIDAD ==========

  /**
   * Override simple de setVisibility (sin clustering)
   */
  override setVisibility(visible: boolean): void {
    if (!this.map) return;

    this.isVisible = visible;
    const visibility = visible ? 'visible' : 'none';

    if (this.map.getLayer(this.mainLayerId)) {
      this.map.setLayoutProperty(this.mainLayerId, 'visibility', visibility);
    }
  }
}
